import { ApolloError, useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useAuth } from '@guildeducationinc/guild-auth';
import { ProgramData } from '@src/typings/mfeCatalogTools';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import { GET_COACH_RECOMMENDATIONS } from '@src/graphql/home/queries/getCoachRecommendations';

/**
 * Fetch coach recommendations from catalog service
 *
 * @packageDocumentation
 */

interface CoachRecommendationMessage {
  message: string;
}

export interface RecommendationsType {
  program: ProgramData;
}

export interface RecommendationsResponse {
  coachRecommendations: RecommendationsType[];
  coachRecommendationMessage: CoachRecommendationMessage;
}

const useGetCoachRecommendations = () => {
  const { user } = useAuth();

  const onError = (error: ApolloError): void => {
    datadogRum.addError(error, {
      message: `There was an error getting coach recommendations data from the catalog service: ${error}`,
      type: 'catalog-service',
    });
  };

  const {
    data: { coachRecommendations = [], coachRecommendationMessage: message } = {},
    error,
    loading,
  } = useQuery<RecommendationsResponse>(GET_COACH_RECOMMENDATIONS, {
    context: {
      server: Server.CATALOG,
    },
    onError,
    skip: !user,
  });

  return {
    coachRecommendations,
    loading,
    message,
    error,
  };
};

export default useGetCoachRecommendations;
